import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { MarkdownContent } from '@/components/markdown-content';
import { toast } from '@/components/ui/sonner';
import { usePersonaMarkdown, useUpdatePersonaGuide } from '@/hooks/use-personas';

interface PersonaGuideEditorProps {
	personaId: string;
	/**
	 * The version being edited, fixed when editing starts. Its text is loaded by version, so the
	 * draft and the version it is saved over always match, and the save is refused if the persona
	 * has moved on since.
	 */
	baseVersion: string;
	/** Leaves edit mode, after a save or a cancel. */
	onDone: () => void;
}

const normalize = (text: string) => text.replace(/\r\n?/g, '\n').trim();

/**
 * Edits the guide's markdown, the exact text the AI reads. Saving makes it the live, validated
 * version; the version it replaces stays in the persona's history.
 */
export function PersonaGuideEditor({ personaId, baseVersion, onDone }: PersonaGuideEditorProps) {
	const { data: markdown, isLoading, isError } = usePersonaMarkdown(personaId, baseVersion);

	if (isLoading) {
		return (
			<div className='flex flex-col gap-2'>
				<Skeleton className='h-5 w-48' />
				<Skeleton className='h-3 w-full' />
				<Skeleton className='h-3 w-3/4' />
			</div>
		);
	}
	if (isError || markdown === undefined) {
		return (
			<div className='flex items-center justify-between gap-3'>
				<p className='text-sm text-muted-foreground'>Couldn't load the guide to edit. Try again.</p>
				<Button variant='outline' className='rounded-full' onClick={onDone}>
					Back
				</Button>
			</div>
		);
	}
	return <GuideDraft personaId={personaId} baseVersion={baseVersion} markdown={markdown} onDone={onDone} />;
}

interface GuideDraftProps extends PersonaGuideEditorProps {
	markdown: string;
}

function GuideDraft({ personaId, baseVersion, markdown, onDone }: GuideDraftProps) {
	const [draft, setDraft] = useState(markdown);
	const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
	const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
	const updateGuide = useUpdatePersonaGuide();

	const isDirty = normalize(draft) !== normalize(markdown);
	const canSave = isDirty && normalize(draft).length > 0 && !updateGuide.isPending;

	// Closing or reloading the tab would lose the edit: ask first.
	useEffect(() => {
		if (!isDirty) return;
		const warn = (event: BeforeUnloadEvent) => event.preventDefault();
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [isDirty]);

	const handleSave = async () => {
		try {
			await updateGuide.mutateAsync({ id: personaId, markdown: draft, baseVersion });
			toast.success('Persona guide saved. The AI uses it from its next reply.');
			onDone();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to save the persona guide');
		}
	};

	const handleCancel = () => (isDirty ? setConfirmDiscardOpen(true) : onDone());

	return (
		<>
			<Tabs defaultValue='write' className='gap-4'>
				<div className='flex items-center justify-between gap-3'>
					<TabsList>
						<TabsTrigger value='write'>Write</TabsTrigger>
						<TabsTrigger value='preview'>Preview</TabsTrigger>
					</TabsList>
					<div className='flex items-center gap-2'>
						<Button variant='outline' className='rounded-full' onClick={handleCancel} disabled={updateGuide.isPending}>
							Cancel
						</Button>
						<Button className='rounded-full gap-1.5' onClick={() => setConfirmSaveOpen(true)} disabled={!canSave}>
							{updateGuide.isPending && <Loader2 className='size-3.5 animate-spin' />}
							Save
						</Button>
					</div>
				</div>
				<TabsContent value='write'>
					<Textarea
						aria-label='Persona guide markdown'
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						className='min-h-[70vh] font-mono text-xs leading-relaxed'
						disabled={updateGuide.isPending}
					/>
				</TabsContent>
				<TabsContent value='preview'>
					{normalize(draft) ? (
						<MarkdownContent text={draft} className='text-sm text-foreground' />
					) : (
						<p className='text-sm text-muted-foreground'>The guide is empty.</p>
					)}
				</TabsContent>
			</Tabs>

			<AlertDialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Save and make this guide live?</AlertDialogTitle>
						<AlertDialogDescription>
							The AI uses this guide from its next reply. The current version is kept in the persona's history.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep editing</AlertDialogCancel>
						<AlertDialogAction onClick={handleSave}>Save</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Discard your changes?</AlertDialogTitle>
						<AlertDialogDescription>Your edits to the guide are not saved and will be lost.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep editing</AlertDialogCancel>
						<AlertDialogAction onClick={onDone}>Discard</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
